import { CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE } from '../conference/actionTypes';
import { MUTE_REMOTE_PARTICIPANT, PARTICIPANT_ID_CHANGED } from '../participants/actionTypes';
import { ReducerRegistry, set } from '../redux';

import {
    SET_NO_SRC_DATA_NOTIFICATION_UID,
    TRACK_ADDED,
    TRACK_CREATE_CANCELED,
    TRACK_CREATE_ERROR,
    TRACK_NO_DATA_FROM_SOURCE,
    TRACK_REMOVED,
    TRACK_UPDATED,
    TRACK_UPDATE_LAST_VIDEO_MEDIA_EVENT,
    TRACK_WILL_CREATE
} from './actionTypes';

/**
 * @typedef {Object} Track
 * @property {(MHLocalTrack|MHRemoteTrack)} [mhTrack] - The associated
 * {@code MHTrack} instance. Optional for local tracks if those are still
 * being created (i.e. {@code getUserMedia} is still in progress).
 * @property {Promise} [gumProcess] - If a local track is still being created,
 * it will have no {@code MHTrack}, but a {@code gumProcess} set to a
 * {@code Promise} with and extra {@code cancel()}.
 * @property {boolean} local=false - If the track is local.
 * @property {MEDIA_TYPE} mediaType=false - The media type of the track.
 * @property {boolean} mirror=false - The indicator which determines whether the
 * display/rendering of the track should be mirrored. It only makes sense in the
 * context of video (at least at the time of this writing).
 * @property {boolean} muted=false - If the track is muted.
 * @property {(string|undefined)} participantId - The ID of the participant whom
 * the track belongs to.
 * @property {boolean} videoStarted=false - If the video track has already
 * started to play.
 * @property {(VIDEO_TYPE|undefined)} videoType - The type of video track if
 * any.
 */

/**
 * Reducer function for a single track.
 *
 * @param {Track|undefined} state - Track to be modified.
 * @param {Object} action - Action object.
 * @param {string} action.type - Type of action.
 * @param {string} action.name - Name of last media event.
 * @param {string} action.newValue - New participant ID value (in this
 * particular case).
 * @param {string} action.oldValue - Old participant ID value (in this
 * particular case).
 * @param {Track} action.track - Information about track to be changed.
 * @param {Participant} action.participant - Information about participant.
 * @returns {Track|undefined}
 */
function track(state, action) {
    switch (action.type) {
    case PARTICIPANT_ID_CHANGED:
        if (state.participantId === action.oldValue) {
            return {
                ...state,
                participantId: action.newValue
            };
        }
        break;

    case MUTE_REMOTE_PARTICIPANT: {
        if (!state.local && state.participantId === action.id && state.mediaType === action.mediaType) {
            return {
                ...state,
                muted: true
            };
        }
        break;
    }

    case TRACK_UPDATED: {
        const t = action.track;

        if (state.mhTrack === t.mhTrack) {
            // Make sure that there's an actual update in order to reduce the
            // risk of unnecessary React Component renders.
            for (const p in t) {
                if (state[p] !== t[p]) {
                    // There's an actual update.
                    return {
                        ...state,
                        ...t
                    };
                }
            }
        }
        break;
    }
    case TRACK_UPDATE_LAST_VIDEO_MEDIA_EVENT: {
        const t = action.track;

        if (state.mhTrack === t) {
            if (state.lastMediaEvent !== action.name) {

                return {
                    ...state,
                    lastMediaEvent: action.name
                };
            }
        }
        break;
    }
    case TRACK_NO_DATA_FROM_SOURCE: {
        const t = action.track;

        if (state.mhTrack === t.mhTrack) {
            const isReceivingData = t.mhTrack?.isReceivingData();

            if (state.isReceivingData !== isReceivingData) {
                return {
                    ...state,
                    isReceivingData
                };
            }
        }
        break;
    }
    }

    return state;
}

/**
 * Listen for actions that mutate (e.g. Add, remove) local and remote tracks.
 */
ReducerRegistry.register('features/base/tracks', (state = [], action) => {
    switch (action.type) {
    case PARTICIPANT_ID_CHANGED:
    case MUTE_REMOTE_PARTICIPANT:
    case TRACK_NO_DATA_FROM_SOURCE:
    case TRACK_UPDATE_LAST_VIDEO_MEDIA_EVENT:
    case TRACK_UPDATED:
        return state.map(t => track(t, action));

    case TRACK_ADDED: {
        let withoutTrackStub = state;

        if (action.track.local) {
            withoutTrackStub
                = state.filter(
                    t => !t.local || t.mediaType !== action.track.mediaType);
        }

        return [ ...withoutTrackStub, action.track ];
    }

    case CONFERENCE_LEFT:
    case CONFERENCE_WILL_LEAVE: {
        // Every remote track belongs to the conference being left. Normally the
        // library removes them one by one as the room drops its occupants, but
        // that only happens when ChatRoom.leave() actually runs: a room that was
        // never joined, or one whose session had already died, is torn down
        // without a single TRACK_REMOVED.
        //
        // The leftovers are not merely dead tiles. The local participant keeps a
        // stable id across rooms (mod_muc_user_persistence), and TRACK_ADDED
        // appends rather than replaces, so a stale entry sits in front of the
        // real one and every "give me this participant's video" lookup finds the
        // corpse first - which is why somebody who had moved between the main
        // room, backstage or the audience could come back with no picture and no
        // sound. Local tracks are kept: they are the devices carried into the
        // next room.
        const withoutRemote = state.filter(t => t.local);

        return withoutRemote.length === state.length ? state : withoutRemote;
    }

    case TRACK_CREATE_CANCELED:
    case TRACK_CREATE_ERROR: {
        return state.filter(t => !t.local || t.mediaType !== action.trackType);
    }

    case TRACK_REMOVED:
        return state.filter(t => t.mhTrack !== action.track.mhTrack);

    case TRACK_WILL_CREATE:
        return [ ...state, action.track ];

    default:
        return state;
    }
});

/**
 * Listen for actions that mutate the no-src-data state, like the current notification id.
 */
ReducerRegistry.register('features/base/no-src-data', (state = {}, action) => {
    switch (action.type) {
    case SET_NO_SRC_DATA_NOTIFICATION_UID:
        return set(state, 'noSrcDataNotificationUid', action.uid);

    default:
        return state;
    }
});

